import { useEffect, useMemo, useRef, useState } from "react";
import DocumentContratClient from "./DocumentContratClient";
const aujourd = () => new Date().toISOString().slice(0, 10);
const n = (v) => Number(v) || 0;
const euro = (v) =>
  n(v).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const finContrat = (debut, mois) => {
  if (!debut || !mois) return "";
  const d = new Date(`${debut}T12:00:00`);
  d.setMonth(d.getMonth() + Number(mois));
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};
const nouveau = (user, parent = null) => ({
  id: "",
  reference: "",
  libelle: "",
  contrat_precedent_id: parent?.id || "",
  commercial_id: user?.id || parent?.commercial_id || "",
  contact_id: "",
  date_contrat: aujourd(),
  date_debut: parent?.date_fin
    ? new Date(new Date(`${parent.date_fin}T12:00:00`).getTime() + 86400000)
        .toISOString()
        .slice(0, 10)
    : aujourd(),
  duree_mois: 12,
  date_fin: "",
  montant_annuel_ttc: parent?.montant_annuel_ttc || 0,
  statut: "BROUILLON",
  type_contrat: parent ? "RENOUVELLEMENT" : "INITIAL",
  observations: "",
  contenu: "",
  conditions_particulieres: "",
  presentation_client: { destinataires: [], copie_commercial: true },
});
const champ = {
  padding: 9,
  border: "1px solid #bbb",
  borderRadius: 6,
  width: "100%",
  boxSizing: "border-box",
};

export default function ContratsCRM({
  supabaseClient,
  groupe,
  club,
  contacts = [],
  piecesClub = [],
  utilisateur,
  contratInitialId,
  renouvelerInitialId,
  onContactEnregistre,
  onRetour,
}) {
  const clubId = club?.id || club?.identifiant;
  const [liste, setListe] = useState([]);
  const [form, setForm] = useState(null);
  const [pieces, setPieces] = useState(piecesClub);
  const [liees, setLiees] = useState([]);
  const [config, setConfig] = useState(null);
  const [commerciaux, setCommerciaux] = useState([]);
  const [versions, setVersions] = useState([]);
  const [pdfUrl, setPdfUrl] = useState("");
  const [destinatairesSignature, setDestinatairesSignature] = useState([]);
  const [copieCommercial, setCopieCommercial] = useState(true);
  const [signatureStatuts, setSignatureStatuts] = useState({});
  const [message, setMessage] = useState("");
  const [charge, setCharge] = useState(false);
  const [apercu, setApercu] = useState(false);
  const [contactEdite, setContactEdite] = useState(null);
  const contactRef = useRef(null);
  const chargerListe = async () => {
    const [r, suivi] = await Promise.all([supabaseClient.rpc("contrats_crm_lister", { p_club_id: clubId, p_groupe: groupe }), supabaseClient.rpc("contrat_signature_statuts", { p_groupe: groupe })]);
    if (r.error) throw r.error;
    setListe(r.data || []);
    if (!suivi.error) setSignatureStatuts(Object.fromEntries((suivi.data || []).map((item) => [item.contrat_id, item])));
  };
  useEffect(() => {
    setPieces(piecesClub || []);
  }, [piecesClub]);
  useEffect(() => {
    (async () => {
      try {
        const [r, c, suivi] = await Promise.all([
          supabaseClient.rpc("contrats_crm_lister", {
            p_club_id: clubId,
            p_groupe: groupe,
          }),
          supabaseClient.functions.invoke("devis-documents", {
            body: { action: "group-config", groupe },
          }),
          supabaseClient.rpc("contrat_signature_statuts", { p_groupe: groupe }),
        ]);
        if (r.error || c.error) throw r.error || c.error;
        setListe(r.data || []);
        setConfig(c.data?.configuration || null);
        setCommerciaux(c.data?.commerciaux || []);
        if (!suivi.error) setSignatureStatuts(Object.fromEntries((suivi.data || []).map((item) => [item.contrat_id, item])));
      } catch {
        setMessage("Impossible de charger les contrats.");
      }
    })();
  }, [supabaseClient, groupe, clubId]);
  useEffect(() => {
    if (contratInitialId === "__NEW__") creer();
    else if (contratInitialId) ouvrir(contratInitialId);
  }, [contratInitialId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!renouvelerInitialId) return;
    (async () => {
      const r = await supabaseClient.rpc("contrats_crm_charger", {
        p_contrat_id: renouvelerInitialId,
      });
      if (r.data) creer(r.data.contrat);
    })();
  }, [renouvelerInitialId]); // eslint-disable-line react-hooks/exhaustive-deps
  const ouvrir = async (id) => {
    setCharge(true);
    const [r, v] = await Promise.all([
      supabaseClient.rpc("contrats_crm_charger", { p_contrat_id: id }),
      supabaseClient.functions.invoke("contrat-documents", {
        body: { action: "list-versions", contratId: id },
      }),
    ]);
    setCharge(false);
    if (r.error || !r.data) return setMessage("Contrat introuvable.");
    setForm({
      ...r.data.contrat,
      type_contrat: r.data.contrat.type_contrat || "INITIAL",
      presentation_client: r.data.contrat.presentation_client || {},
    });
    setLiees(
      (r.data.pieces || []).map((p) => ({
        piece_id: p.id,
        inclure_envoi: true,
      })),
    );
    setDestinatairesSignature(
      r.data.contrat.contact_id ? [r.data.contrat.contact_id] : [],
    );
    setVersions(v.data?.versions || r.data.versions || []);
    setPdfUrl(v.data?.versions?.[0]?.pdfUrl || "");
    setApercu(false);
  };
  const creer = (parent = null) => {
    const f = nouveau(utilisateur, parent);
    f.date_fin = finContrat(f.date_debut, f.duree_mois);
    if (parent) {
      f.libelle = parent.libelle || "";
      f.contenu = parent.contenu || "";
      f.conditions_particulieres = parent.conditions_particulieres || "";
    }
    setForm(f);
    setLiees([]);
    setVersions([]);
    setApercu(false);
  };
  const commercial = commerciaux.find((c) => c.id === form?.commercial_id);
  const contact = contacts.find((c) => c.id === form?.contact_id);
  const doc = useMemo(
    () =>
      form
        ? {
            emetteur: {
              groupe_nom: config?.nom,
              nom_commercial: config?.nom_commercial,
              raison_sociale: config?.raison_sociale,
              adresse: config?.adresse,
              code_postal: config?.code_postal,
              ville: config?.ville,
              telephone: config?.telephone,
              email: config?.email,
              siret: config?.siret,
              tva_intracom: config?.tva_intracom,
              mentions_legales: config?.mentions_legales,
              logo_path: config?.logo_devis_path,
            },
            commercial: {
              nom: commercial
                ? `${commercial.prenom || ""} ${commercial.nom_famille || commercial.nom || ""}`.trim()
                : "",
              fonction: commercial?.fonction || "",
              telephone: commercial?.telephone || "",
              email: commercial?.email || "",
            },
            contrat: form,
            client: {
              nom: club.nom,
              adresse: club.adresse,
              code_postal: club.code_postal,
              ville: club.ville,
            },
            contact: contact
              ? {
                  nom: contact.nom,
                  fonction: contact.fonction,
                  email: contact.mail,
                  telephone: contact.telephone,
                }
              : null,
            banque: {
              titulaire_compte: config?.titulaire_compte,
              iban: config?.iban,
              bic: config?.bic,
            },
          }
        : null,
    [form, config, commercial, contact, club],
  );
  const modifier = (k, v) =>
    setForm((f) => ({
      ...f,
      [k]: v,
      ...(k === "date_debut" || k === "duree_mois"
        ? {
            date_fin: finContrat(
              k === "date_debut" ? v : f.date_debut,
              k === "duree_mois" ? v : f.duree_mois,
            ),
          }
        : {}),
    }));
  const enregistrer = async () => {
    if (!form.libelle?.trim() || !form.date_debut || !form.date_fin)
      return setMessage("Libellé, date de début et date de fin obligatoires.");
    setCharge(true);
    const r = await supabaseClient.rpc("contrats_crm_enregistrer", {
      p_groupe: groupe,
      p_club_id: clubId,
      p_contrat: {
        ...form,
        presentation_client: {
          ...(form.presentation_client || {}),
          commercial: doc.commercial,
          emetteur: doc.emetteur,
        },
      },
      p_pieces: liees,
    });
    setCharge(false);
    if (r.error)
      return setMessage(
        `Enregistrement impossible : ${r.error.message || "erreur"}`,
      );
    setForm((f) => ({
      ...f,
      id: r.data,
      reference: f.reference || "Référence attribuée",
    }));
    setMessage("Contrat enregistré.");
    await chargerListe();
    await ouvrir(r.data);
  };
  const televerser = async (files) => {
    const selection = Array.from(files || []);
    if (!selection.length) return;
    setCharge(true);
    let ajoutes = 0;
    for (const fichier of selection) {
      const chemin = `${groupe}/${clubId}/${Date.now()}-${crypto.randomUUID()}-${fichier.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const up = await supabaseClient.storage
        .from("pieces-clubs")
        .upload(chemin, fichier, { contentType: fichier.type, upsert: false });
      if (up.error) {
        console.log("ERREUR STORAGE PJ CONTRAT :", up.error);
        setMessage(
          `Échec du téléversement : ${up.error.message || "accès Storage refusé"}`,
        );
        continue;
      }
      const ins = await supabaseClient
        .from("pieces_jointes")
        .insert({
          club_id: clubId,
          nom: fichier.name,
          type: fichier.type,
          chemin,
          url: null,
        })
        .select()
        .single();
      if (ins.error) {
        console.log("ERREUR ENREGISTREMENT PJ CONTRAT :", ins.error);
        await supabaseClient.storage.from("pieces-clubs").remove([chemin]);
        setMessage(
          `Échec du téléversement : ${ins.error.message || "enregistrement refusé"}`,
        );
        continue;
      }
      const acces = await supabaseClient.storage
        .from("pieces-clubs")
        .createSignedUrl(chemin, 3600);
      const piece = {
        ...ins.data,
        url: acces.data?.signedUrl || "",
        _nouvelleContrat: true,
      };
      setPieces((p) => [piece, ...p]);
      setLiees((p) => [...p, { piece_id: ins.data.id, inclure_envoi: true }]);
      ajoutes += 1;
    }
    if (ajoutes) setMessage(`${ajoutes} pièce(s) ajoutée(s) au contrat.`);
    setCharge(false);
  };
  const ouvrirPiece = async (piece) => {
    if (!piece?.chemin) {
      if (piece?.url) window.open(piece.url, "_blank", "noopener,noreferrer");
      return;
    }
    const r = await supabaseClient.storage
      .from("pieces-clubs")
      .createSignedUrl(piece.chemin, 300);
    if (r.error || !r.data?.signedUrl) {
      console.log("ERREUR OUVERTURE PJ CONTRAT :", r.error);
      return setMessage(
        `Ouverture impossible : ${r.error?.message || "accès Storage refusé"}`,
      );
    }
    window.open(r.data.signedUrl, "_blank", "noopener,noreferrer");
  };
  const ajouterContact = async () => {
    if (!contactEdite?.nom?.trim()) return;
    const r = await supabaseClient
      .from("contacts")
      .insert({
        club_id: clubId,
        nom: contactEdite.nom.trim(),
        fonction: contactEdite.fonction || "",
        telephone: contactEdite.telephone || "",
        mail: contactEdite.mail || "",
      })
      .select()
      .single();
    if (r.error) return setMessage("Contact non enregistré.");
    onContactEnregistre?.(r.data);
    setForm((f) => ({ ...f, contact_id: r.data.id }));
    setContactEdite(null);
    setMessage("Contact ajouté au contrat.");
  };
  const generer = async () => {
    if (!form.id) return setMessage("Enregistrez le contrat avant le PDF.");
    setCharge(true);
    const r = await supabaseClient.functions.invoke("contrat-documents", {
      body: { action: "generate", contratId: form.id },
    });
    setCharge(false);
    if (r.error) return setMessage(r.data?.error || "PDF impossible.");
    setMessage("PDF généré et version figée.");
    setVersions((v) => [r.data.version, ...v]);
    setPdfUrl(r.data.pdfUrl || "");
  };
  const envoyer = async () => {
    if (!form.id || !versions.length)
      return setMessage("Générez d'abord une version PDF.");
    const emails = contacts
      .filter((c) => destinatairesSignature.includes(c.id) && c.mail)
      .map((c) => c.mail);
    if (!emails.length)
      return setMessage("Sélectionnez au moins un destinataire avec email.");
    setCharge(true);
    const r = await supabaseClient.functions.invoke("contrat-documents", {
      body: { action: "create-link", contratId: form.id, destinataires: emails, copieCommercial },
    });
    setCharge(false);
    if (r.error) return setMessage(r.data?.error || "Lien impossible.");
    const annexes = liees
      .filter((x) => x.inclure_envoi !== false)
      .map((x) => pieces.find((p) => p.id === x.piece_id)?.nom)
      .filter(Boolean);
    const objet = `Contrat ${form.reference || "club"}`;
    const texte = `Bonjour,\n\nVotre contrat est disponible pour consultation et signature : ${r.data.lien}\n\nDocument principal : Contrat PDF\nAnnexes sélectionnées : ${annexes.join(", ") || "Aucune"}\n\nCordialement,\n${doc.commercial.nom}`;
    const cc =
      copieCommercial && doc.commercial.email
        ? `&cc=${encodeURIComponent(doc.commercial.email)}`
        : "";
    window.location.href = `mailto:${encodeURIComponent(emails.join(","))}?subject=${encodeURIComponent(objet)}&body=${encodeURIComponent(texte)}${cc}`;
    setMessage("Lien sécurisé préparé.");
    const suivi = await supabaseClient.functions.invoke("contrat-documents", { body: { action: "list-versions", contratId: form.id } });
    if (!suivi.error) setVersions(suivi.data?.versions || []);
  };
  if (!form)
    return (
      <main style={{ padding: 20, background: "#f3f5f7", minHeight: "100vh" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h2>Contrats — {club.nom}</h2>
          <div>
            <button onClick={() => creer()}>+ NOUVEAU CONTRAT</button>{" "}
            <button onClick={onRetour}>RETOUR CLUB</button>
          </div>
        </div>
        {message && <p>{message}</p>}
        <table
          style={{
            width: "100%",
            background: "white",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr>
              {[
                "Filiation",
                "Référence",
                "Date",
                "Début",
                "Fin",
                "Durée",
                "TTC/an",
                "Statut",
                "Signature",
                "Commercial",
                "PJ",
                "Document",
                "Actions",
              ].map((h) => (
                <th
                  key={h}
                  style={{ padding: 8, borderBottom: "1px solid #ccc" }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {liste.map((c) => (
              <tr key={c.id}>
                <td>
                  {c.type_contrat === "RENOUVELLEMENT"
                    ? `↳ Renouvellement de ${c.parent_reference || "—"}`
                    : "Contrat initial"}
                </td>
                <td>{c.reference}</td>
                <td>{c.date_contrat || "—"}</td>
                <td>{c.date_debut || "—"}</td>
                <td>{c.date_fin || "—"}</td>
                <td>{c.duree_mois ? `${c.duree_mois} mois` : "—"}</td>
                <td>{euro(c.montant_annuel_ttc)} €</td>
                <td>{c.statut}</td>
                <td>{(signatureStatuts[c.id]?.statut || "NON_ENVOYE").replaceAll("_", " ")}</td>
                <td>{c.commercial_nom || "—"}</td>
                <td>{c.pieces_count}</td>
                <td>
                  {c.versions_count} version(s){c.signe ? " — signé" : ""}
                </td>
                <td>
                  <button onClick={() => ouvrir(c.id)}>OUVRIR</button>{" "}
                  <button
                    onClick={async () => {
                      const r = await supabaseClient.rpc(
                        "contrats_crm_charger",
                        { p_contrat_id: c.id },
                      );
                      if (r.data) creer({ ...c, ...r.data.contrat });
                    }}
                  >
                    RENOUVELER
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {liste.length === 0 && <p>Aucun contrat pour ce club.</p>}
      </main>
    );
  return (
    <main style={{ padding: 20, background: "#f3f5f7", minHeight: "100vh" }}>
      <div style={{ background: "white", padding: 20, borderRadius: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h2>
            {form.type_contrat === "RENOUVELLEMENT"
              ? "Renouvellement"
              : "Contrat initial"}{" "}
            — {club.nom}
          </h2>
          <button onClick={() => setForm(null)}>RETOUR LISTE</button>
        </div>
        {message && (
          <p style={{ background: "#fff3cd", padding: 9 }}>{message}</p>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))",
            gap: 10,
          }}
        >
          <label>
            Référence
            <input
              style={champ}
              placeholder="Automatique si vide"
              value={form.reference || ""}
              onChange={(e) => modifier("reference", e.target.value)}
            />
          </label>
          <label>
            Libellé *
            <input
              style={champ}
              value={form.libelle || ""}
              onChange={(e) => modifier("libelle", e.target.value)}
            />
          </label>
          <label>
            Commercial
            <select
              style={champ}
              value={form.commercial_id || ""}
              onChange={(e) => modifier("commercial_id", e.target.value)}
            >
              <option value="">Choisir</option>
              {commerciaux.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.prenom || ""} {c.nom_famille || c.nom || ""}
                </option>
              ))}
            </select>
          </label>
          <label>
            Contact principal
            <select
              style={champ}
              value={form.contact_id || ""}
              onChange={(e) => modifier("contact_id", e.target.value)}
            >
              <option value="">Aucun</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom}
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                setContactEdite({
                  nom: "",
                  fonction: "",
                  telephone: "",
                  mail: "",
                });
                setTimeout(
                  () =>
                    contactRef.current?.scrollIntoView({ behavior: "smooth" }),
                  0,
                );
              }}
            >
              + AJOUTER UN CONTACT
            </button>
          </label>
          <label>
            Date du contrat
            <input
              type="date"
              style={champ}
              value={form.date_contrat || ""}
              onChange={(e) => modifier("date_contrat", e.target.value)}
            />
          </label>
          <label>
            Date de début
            <input
              type="date"
              style={champ}
              value={form.date_debut || ""}
              onChange={(e) => modifier("date_debut", e.target.value)}
            />
          </label>
          <label>
            Durée
            <select
              style={champ}
              value={
                [12, 24, 36].includes(Number(form.duree_mois))
                  ? form.duree_mois
                  : "AUTRE"
              }
              onChange={(e) =>
                modifier(
                  "duree_mois",
                  e.target.value === "AUTRE" ? form.duree_mois : e.target.value,
                )
              }
            >
              <option value="12">1 an</option>
              <option value="24">2 ans</option>
              <option value="36">3 ans</option>
              <option value="AUTRE">Autre</option>
            </select>
            {![12, 24, 36].includes(Number(form.duree_mois)) && (
              <input
                type="number"
                min="1"
                style={champ}
                value={form.duree_mois || ""}
                onChange={(e) => modifier("duree_mois", e.target.value)}
              />
            )}
          </label>
          <label>
            Date de fin
            <input
              type="date"
              style={champ}
              value={form.date_fin || ""}
              onChange={(e) => modifier("date_fin", e.target.value)}
            />
          </label>
          <label>
            Montant annuel TTC
            <input
              type="number"
              min="0"
              step="0.01"
              style={champ}
              value={form.montant_annuel_ttc || 0}
              onChange={(e) => modifier("montant_annuel_ttc", e.target.value)}
            />
          </label>
          <label>
            Statut
            <select
              style={champ}
              value={form.statut}
              onChange={(e) => modifier("statut", e.target.value)}
            >
              {[
                "BROUILLON",
                "PROPOSE",
                "ENVOYE",
                "SIGNE",
                "ACTIF",
                "ARRIVANT_ECHEANCE",
                "TERMINE",
                "RESILIE",
                "REFUSE",
              ].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
        </div>
        <label>
          CONTENU / CONDITIONS DU CONTRAT
          <textarea
            rows="12"
            style={champ}
            value={form.contenu || ""}
            onChange={(e) => modifier("contenu", e.target.value)}
          />
        </label>
        <label>
          Conditions particulières
          <textarea
            rows="4"
            style={champ}
            value={form.conditions_particulieres || ""}
            onChange={(e) =>
              modifier("conditions_particulieres", e.target.value)
            }
          />
        </label>
        <label>
          Observations
          <textarea
            rows="3"
            style={champ}
            value={form.observations || ""}
            onChange={(e) => modifier("observations", e.target.value)}
          />
        </label>
        {contactEdite && (
          <section
            ref={contactRef}
            style={{ padding: 12, border: "1px solid #1565c0" }}
          >
            <h3>Ajouter un contact</h3>
            {["nom", "fonction", "telephone", "mail"].map((k) => (
              <input
                key={k}
                placeholder={k}
                style={{ ...champ, marginBottom: 6 }}
                value={contactEdite[k]}
                onChange={(e) =>
                  setContactEdite({ ...contactEdite, [k]: e.target.value })
                }
              />
            ))}
            <button onClick={ajouterContact}>ENREGISTRER LE CONTACT</button>{" "}
            <button onClick={() => setContactEdite(null)}>ANNULER</button>
          </section>
        )}
        <h3>DOCUMENTS DU CONTRAT</h3>
        {form.type_contrat === "RENOUVELLEMENT" && (
          <p>
            Sélectionnez uniquement les PJ de l'ancien contrat à reprendre.
            Aucune n'est recopiée automatiquement.
          </p>
        )}
        <label
          style={{ display: "inline-block", padding: 9, background: "#eef5ff" }}
        >
          + AJOUTER DES FICHIERS
          <input
            hidden
            multiple
            type="file"
            onChange={(e) => {
              televerser(e.target.files);
              e.target.value = "";
            }}
          />
        </label>
        <div style={{ overflowX: "auto", marginTop: 8 }}>
          <table style={{ width: "100%" }}>
            <tbody>
              {liees.map((l) => {
                const p = pieces.find((x) => x.id === l.piece_id);
                return p ? (
                  <tr key={p.id}>
                    <td>📄 {p.nom}</td>
                    <td>{p.type || "fichier"}</td>
                    <td>
                      {p._nouvelleContrat ? "Ajout contrat" : "Fiche club"}
                    </td>
                    <td>
                      <label>
                        <input
                          type="checkbox"
                          checked={l.inclure_envoi !== false}
                          onChange={(e) =>
                            setLiees((x) =>
                              x.map((i) =>
                                i.piece_id === p.id
                                  ? { ...i, inclure_envoi: e.target.checked }
                                  : i,
                              ),
                            )
                          }
                        />{" "}
                        Inclure dans l'envoi
                      </label>
                    </td>
                    <td>
                      <button onClick={() => ouvrirPiece(p)}>OUVRIR</button>{" "}
                      <button
                        onClick={() =>
                          setLiees((x) => x.filter((i) => i.piece_id !== p.id))
                        }
                      >
                        RETIRER
                      </button>
                    </td>
                  </tr>
                ) : null;
              })}
            </tbody>
          </table>
        </div>
        {liees.length === 0 && <p>Aucun document annexe associé.</p>}
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: "pointer", fontWeight: "bold" }}>
            AJOUTER DEPUIS LES PJ DU CLUB
          </summary>
          {pieces
            .filter((p) => !liees.some((l) => l.piece_id === p.id))
            .map((p) => (
              <div key={p.id} style={{ padding: 6 }}>
                📄 {p.nom}{" "}
                <button
                  onClick={() =>
                    setLiees((x) => [
                      ...x,
                      { piece_id: p.id, inclure_envoi: true },
                    ])
                  }
                >
                  AJOUTER AU CONTRAT
                </button>{" "}
                <button onClick={() => ouvrirPiece(p)}>OUVRIR</button>
              </div>
            ))}
        </details>
        {pdfUrl && (
          <section
            style={{ marginTop: 15, padding: 12, background: "#eef5ff" }}
          >
            <strong>DOCUMENT PRINCIPAL DU CONTRAT</strong>
            <div>Contrat n° {form.reference || "—"} — Version {versions[0]?.numero_version || "—"} — Généré le {versions[0]?.genere_at ? new Date(versions[0].genere_at).toLocaleString("fr-FR") : "—"}</div>
            <div>
              <a href={pdfUrl} target="_blank" rel="noreferrer">
                OUVRIR LE PDF
              </a>
            </div>
          </section>
        )}
        <section style={{ marginTop: 15, padding: 14, border: "3px solid #0d5f4d", borderRadius: 8, background: "#f4fbf8" }}>
          <h3 style={{ marginTop: 0 }}>SIGNATURE CONTRAT</h3>
          {(() => { const s = versions[0]?.signature || { statut: "NON_ENVOYE" }; const labels = { NON_ENVOYE: "⚪ NON ENVOYÉ", ENVOYE: "🔵 ENVOYÉ", CONSULTE: "🟠 CONSULTÉ", SIGNE: "🟢 SIGNÉ", REFUSE: "🔴 REFUSÉ", EXPIRE: "⚫ EXPIRÉ" }; return <><p style={{ fontSize: 18, fontWeight: "bold" }}>{labels[s.statut] || s.statut}</p>{s.envoye_at && <p>Envoyé le : {new Date(s.envoye_at).toLocaleString("fr-FR")}</p>}{s.consulte_at && <p>Première consultation : {new Date(s.consulte_at).toLocaleString("fr-FR")}</p>}{s.statut === "REFUSE" && s.motif_refus && <p>Motif : {s.motif_refus}</p>}{s.statut === "EXPIRE" && s.expires_at && <p>Expiration : {new Date(s.expires_at).toLocaleString("fr-FR")}</p>}{s.signe_at && <p><strong>CONTRAT SIGNÉ</strong><br/>Signataire : {s.signataire || "—"}<br/>Date : {new Date(s.signe_at).toLocaleDateString("fr-FR")} — Heure : {new Date(s.signe_at).toLocaleTimeString("fr-FR")}<br/>Version : {versions[0]?.numero_version}</p>}{s.pdf_signe && versions[0]?.pdfUrl && <><a href={versions[0].pdfUrl} target="_blank" rel="noreferrer"><button>VOIR LE CONTRAT SIGNÉ</button></a> <a href={versions[0].pdfUrl} download><button>TÉLÉCHARGER</button></a></>}</>; })()}
        </section>
        <section
          style={{ marginTop: 15, padding: 12, border: "1px solid #bbb" }}
        >
          <h3>ENVOYER POUR SIGNATURE</h3>
          <p>
            <strong>Document principal :</strong> ☑ Contrat PDF
          </p>
          <strong>Destinataire(s)</strong>
          {contacts.map((c) => (
            <label key={c.id} style={{ display: "block" }}>
              <input
                type="checkbox"
                disabled={!c.mail}
                checked={destinatairesSignature.includes(c.id)}
                onChange={(e) =>
                  setDestinatairesSignature((x) =>
                    e.target.checked
                      ? [...new Set([...x, c.id])]
                      : x.filter((id) => id !== c.id),
                  )
                }
              />{" "}
              {c.nom} — {c.mail || "email absent"}
            </label>
          ))}
          <label style={{ display: "block", marginTop: 8 }}>
            <input
              type="checkbox"
              checked={copieCommercial}
              onChange={(e) => setCopieCommercial(e.target.checked)}
            />{" "}
            Copie au commercial ({doc.commercial.email || "email absent"})
          </label>
          <strong>Annexes</strong>
          {liees.map((l) => {
            const p = pieces.find((x) => x.id === l.piece_id);
            return p ? (
              <label key={p.id} style={{ display: "block" }}>
                <input
                  type="checkbox"
                  checked={l.inclure_envoi !== false}
                  onChange={(e) =>
                    setLiees((x) =>
                      x.map((i) =>
                        i.piece_id === p.id
                          ? { ...i, inclure_envoi: e.target.checked }
                          : i,
                      ),
                    )
                  }
                />{" "}
                {p.nom}
              </label>
            ) : null;
          })}
        </section>
        <div
          style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 18 }}
        >
          <button disabled={charge || form.verrouille} onClick={enregistrer}>
            ENREGISTRER
          </button>
          <button onClick={() => setApercu(!apercu)}>APERÇU</button>
          <button disabled={charge || !form.id} onClick={generer}>
            GÉNÉRER PDF
          </button>
          <button
            disabled={charge || !form.id || !versions.length}
            onClick={envoyer}
          >
            ENVOYER POUR SIGNATURE
          </button>
          {form.id && (
            <button onClick={() => creer({ ...form, id: form.id })}>
              RENOUVELER LE CONTRAT
            </button>
          )}
        </div>
        {form.verrouille && (
          <p>Contrat signé et verrouillé. Utilisez Renouveler.</p>
        )}
        {versions.length > 0 && (
          <div><strong>Versions conservées :</strong>{versions.map((v) => <span key={v.id} style={{ display: "inline-block", margin: 5, padding: "5px 8px", background: "#eef3f8" }}>v{v.numero_version} — {v.statut} {v.pdfUrl && <a href={v.pdfUrl} target="_blank" rel="noreferrer">OUVRIR</a>}</span>)}</div>
        )}
        {apercu && (
          <section style={{ marginTop: 20, background: "#ddd", padding: 12 }}>
            <DocumentContratClient document={doc} logo={config?.logo || null} />
          </section>
        )}
      </div>
    </main>
  );
}
